import React from "react";
import { useSelector } from "react-redux";

/**
 * Sidebar menu data. Returns a `<React.Fragment>` whose `props.children` is
 * the filtered menu array — that's the contract SidebarContent reads.
 *
 * Gating:
 *  - `permission`     — hidden unless the user has that permission.
 *  - `permissionsAny` — hidden unless the user has AT LEAST ONE of these
 *                       (used by the consolidated hubs that merge several
 *                       permission domains into one nav item).
 *  - `superAdminOnly` — hidden unless the user is a super-admin.
 * Super-admins bypass all of the above. A header with no visible item under it
 * is hidden too.
 *
 * "In the app" groups every section that PUBLISHES into the mobile app, each
 * named after where it lands there (one design since 2026-10-26;
 * ADMIN_PANEL_MAP.md): Notifications (push + the Notifications screen), Token
 * listings (Market › Tokens, Home holdings, the Featured row on Home) and
 * Rewards (the Rewards screen, Settings › Rewards › Redeem code).
 */
const Navdata = () => {
    const is_super_admin = useSelector((s) => s.Profile.is_super_admin);
    const permissions    = useSelector((s) => s.Profile.permissions);
    const can    = (key) => is_super_admin || (key && permissions.includes(key));
    const canAny = (keys) => is_super_admin || (keys || []).some((k) => permissions.includes(k));

    const allMenuItems = [
        { label: "Menu", isHeader: true },
        { id: "overview",      label: "Dashboard",    icon: "ri-focus-3-line",         link: "/overview",      permission: "dashboard.view" },
        { id: "money",         label: "Money",        icon: "ri-exchange-dollar-line", link: "/money",         permissionsAny: ["transactions.view", "paybis.view", "fees.view"] },
        { id: "growth",        label: "Growth",       icon: "ri-smartphone-line",      link: "/growth",        permissionsAny: ["app_metrics.view", "alerts.view"] },
        { id: "voice",         label: "Voice of User", icon: "ri-chat-3-line",         link: "/voice",         permissionsAny: ["feedback.view", "form_submissions.view"] },
        { label: "In the app", isHeader: true },
        { id: "notifications", label: "Notifications", icon: "ri-notification-3-line", link: "/notifications", permission: "notifications.view" },
        { id: "token-listings", label: "Token listings", icon: "ri-coins-line",         link: "/token-listings", permission: "token_listings.view" },
        { id: "rewards",       label: "Rewards",      icon: "ri-gift-line",            link: "/rewards",       permission: "rewards.view" },
        { label: "System", isHeader: true },
        { id: "system-health", label: "Operations",   icon: "ri-pulse-line",           link: "/system-health", permissionsAny: ["service_health.view", "errors.view", "pr_discipline.view"] },
        { id: "coingecko",     label: "CoinGecko API", icon: "ri-line-chart-line",      link: "/coingecko-usage", permission: "coingecko.view" },
        { id: "users",         label: "Team",         icon: "ri-team-line",            link: "/users",         superAdminOnly: true },
    ];

    const visible = (item) => {
        if (item.superAdminOnly) return is_super_admin;
        if (item.permissionsAny) return canAny(item.permissionsAny);
        if (item.permission) return can(item.permission);
        return true;
    };
    const menuItems = allMenuItems.filter((item, i) => {
        if (!item.isHeader) return visible(item);
        // a header shows only when something under it (up to the next header) does
        for (let j = i + 1; j < allMenuItems.length && !allMenuItems[j].isHeader; j++) {
            if (visible(allMenuItems[j])) return true;
        }
        return false;
    });

    return <React.Fragment>{menuItems}</React.Fragment>;
};

export default Navdata;
